class VolumeBasedMessage extends HTMLElement {
  constructor() {
    super();

    this.discountStyle = this.dataset.discountStyle;
    this.optionAwareness = this.dataset.optionAwareness || 'next';
    this.optionString = this.dataset.optionName;
    this.originalMessage = this.dataset.message;
    this.optionIndex = null; // The position in the product.options array of this.optionString
  }

  /**
   * this.optionIndex is the index of the option that matches this.optionString.
   * Basically, dynamically finding the position of the "Units" option. "Units"
   * is subject to change though, which is why it is defined as a theme setting.
   *
   * @param {object} currentProduct
   */
  setOptionIndex(currentProduct) {
    //  The index position won't change once we have it, so bail if it exists.
    if (this.optionIndex) return;

    const hasMatchingOption = (option) =>
      option.toLowerCase() == this.optionString;

    this.optionIndex = currentProduct.options.findIndex(hasMatchingOption);
  }

  /**
   * Get the message based on the next OR current option.
   * Depends on this.optionAwareness.
   *
   * @param {object} currentVariant
   * @param {object} currentProduct only applies when this.optionAwareness == 'next'
   * @param {number} currentVariantIndex only applies when this.optionAwareness == 'next'
   * @returns {string} message
   */
  getOptionMessage(currentVariant, currentProduct, currentVariantIndex) {
    let message = '';
    let targetVariant = null;

    if (this.optionAwareness == 'next') {
      /**
       * Iterate through all variants, starting at current variant. The goal is
       * to find the next variant with the next option.
       */
      for (
        let i = currentVariantIndex;
        i < currentProduct.variants.length;
        i++
      ) {
        const variant = currentProduct.variants[i];

        // Check that the "Units" do NOT match, as we want the next "Units".
        if (
          variant.options[this.optionIndex] !==
          currentVariant.options[this.optionIndex]
        ) {
          // Make an array of the other options that DO match.
          const matchingOptionsArray = variant.options.filter((options) => {
            return currentVariant.options.includes(options);
          });

          /**
           * Check that the array is only 1 less than the options.length. This
           * means that 0/1, 1/2 or 2/3 of the options match. Since we already
           * checked that the "Units" don't match, the others must match in
           * order for this to highlight the variant that is the next "Unit" size.
           */
          if (
            matchingOptionsArray.length ===
            currentVariant.options.length - 1
          ) {
            targetVariant = variant;
            break;
          }
        }
      }
    } else {
      // When this.optionAwareness == 'current', do the following
      targetVariant = currentVariant;
    }

    // Write the volume based message.
    if (targetVariant && targetVariant.compare_at_price) {
      let savings = '';

      if (this.discountStyle == 'percentage') {
        savings =
          Math.round(
            100 - (targetVariant.price / targetVariant.compare_at_price) * 100
          ) + '%';
      }

      if (this.discountStyle == 'dollars') {
        savings = theme.Shopify.formatMoney(
          targetVariant.compare_at_price - targetVariant.price,
          theme.money_format
        );
      }

      message = this.originalMessage
        .replace(
          '[option]',
          `<span class="option">${
            targetVariant.options[this.optionIndex]
          }</span>`
        )
        .replace('[savings]', `<span class="savings">${savings}</span>`);
    } else {
      message = '';
    }

    return message;
  }

  connectedCallback() {
    if (!this.optionString) return;

    document.addEventListener('globalVariantChanged', (e) => {
      const currentVariant = e.detail.variant;
      const currentProduct = e.detail.product;

      this.setOptionIndex(currentProduct);

      // Find current variant's index position in list of all variants.
      const hasMatchingId = (variant) => variant.id === currentVariant.id;
      const currentVariantIndex =
        currentProduct.variants.findIndex(hasMatchingId);

      const message = this.getOptionMessage(
        currentVariant,
        currentProduct,
        currentVariantIndex
      );

      this.innerHTML = message ? `<span>${message}</span>` : '';
    });
  }
}

customElements.define('volume-based-message', VolumeBasedMessage);
